import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Activity, ShieldCheck, ArrowLeft, FileWarning, MapPin, FileDown, QrCode, Inbox, HandHeart,
  Mail, AlertTriangle, Eye, RefreshCw, TrendingUp, Printer, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const Metric = ({ icon: Icon, label, value, sub, testId }) => (
  <Card className="card-lift h-full" data-testid={testId}>
    <CardContent className="p-4 flex items-start gap-3">
      <div className="h-10 w-10 rounded-lg bg-[#1a2744]/5 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-[#1a2744]" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="font-serif font-black text-2xl text-[#1a2744] leading-tight">{value}</p>
        {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

export default function AdminPostLaunchPulse() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snapBusy, setSnapBusy] = useState(false);
  const [snapshot, setSnapshot] = useState(null);

  const load = useCallback(() => {
    setBusy(true);
    api.get('/admin/post-launch-pulse').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true)).finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const printSnapshot = async () => {
    setSnapBusy(true);
    try {
      const r = await api.post('/admin/post-launch-pulse/snapshot');
      setSnapshot(r.data);
      toast.success('Weekly Pulse Snapshot generated');
    } catch { toast.error('Snapshot generation failed'); }
    setSnapBusy(false);
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="pulse-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load Post-Launch Pulse.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const m = data.metrics;

  return (
    <div data-testid="admin-post-launch-pulse">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><Activity className="h-6 w-6 text-[#C9A84C]" /> Post-Launch Pulse</h1>
          <p className="text-sm text-slate-500 mt-1">Private first-week snapshot — how the launch is performing across the campaign tools.</p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Button onClick={printSnapshot} disabled={snapBusy} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="pulse-snapshot-btn">
            {snapBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5 text-[#C9A84C]" />} Print Weekly Pulse Snapshot
          </Button>
          <Button onClick={load} disabled={busy} variant="outline" className="h-11 font-bold btn-press" data-testid="pulse-refresh-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Refresh
          </Button>
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="pulse-back-btn">
            <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1.5" /> Dashboard</Link>
          </Button>
        </div>
      </div>

      {snapshot?.pdf && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50/60 p-4 mt-4" data-testid="pulse-snapshot-result">
          <p className="text-sm font-bold text-emerald-900 mb-2">Weekly Pulse Snapshot ready · {snapshot.week_range}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => window.open(fileUrl(snapshot.pdf), '_blank', 'noopener')} className="h-9 text-xs font-bold btn-press bg-white" data-testid="pulse-snapshot-pdf-btn">
              <FileDown className="h-3.5 w-3.5 mr-1" /> Download PDF
            </Button>
            {snapshot.png && (
              <Button size="sm" variant="outline" onClick={() => window.open(fileUrl(snapshot.png), '_blank', 'noopener')} className="h-9 text-xs font-bold btn-press bg-white" data-testid="pulse-snapshot-png-btn">
                <ImageIcon className="h-3.5 w-3.5 mr-1" /> PNG Preview
              </Button>
            )}
          </div>
          <p className="text-[11px] text-emerald-800/80 mt-2">{snapshot.note}</p>
        </div>
      )}

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-5" data-testid="pulse-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      {/* Attention items */}
      {(data.attention || []).length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-4 mb-5" data-testid="pulse-attention">
          <p className="font-serif font-bold text-amber-900 text-sm flex items-center gap-1.5 mb-2"><AlertTriangle className="h-4 w-4" /> Needs attention</p>
          <div className="space-y-1.5">
            {data.attention.map((a, i) => (
              <Link key={i} to={a.link} className="block text-sm font-semibold text-amber-900 underline underline-offset-4 hover:text-amber-700" data-testid={`pulse-attention-${i}`}>{a.label}</Link>
            ))}
          </div>
        </div>
      )}

      {/* Metrics grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pulse-metrics">
        <Metric icon={FileWarning} label="Issue reports" value={m.issue_reports.total} sub={`${m.issue_reports.last7} in the last 7 days`} testId="pulse-metric-issues" />
        <Metric icon={MapPin} label="Public issues on the map" value={m.public_issues.total} sub="Consent-approved, privacy-safe" testId="pulse-metric-public-issues" />
        <Metric icon={FileDown} label="Flyer downloads" value={m.flyer_downloads.total} sub={`PDF ${m.flyer_downloads.pdf} · Word ${m.flyer_downloads.docx}`} testId="pulse-metric-flyer" />
        <Metric icon={QrCode} label="QR scans" value={m.qr_scans.total} sub={m.qr_scans.top_source ? `Top source: ${m.qr_scans.top_source} · ${m.qr_scans.last7} this week` : 'No scans yet'} testId="pulse-metric-qr" />
        <Metric icon={Inbox} label="Contact messages" value={m.contact_messages.total} sub={`${m.contact_messages.last7} this week · ${m.contact_messages.unread} unread`} testId="pulse-metric-contacts" />
        <Metric icon={HandHeart} label="Volunteer signups" value={m.volunteer_signups.total} sub={`${m.volunteer_signups.last7} in the last 7 days`} testId="pulse-metric-volunteers" />
        <Metric icon={Mail} label="Newsletter signups" value={m.newsletter_signups.total} sub={`${m.newsletter_signups.last7} in the last 7 days`} testId="pulse-metric-newsletter" />
        <Metric icon={TrendingUp} label="Site visits" value={data.visits === null ? '—' : data.visits} sub="Analytics not connected" testId="pulse-metric-visits" />
      </div>

      {/* Setup notes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pulse-analytics-note">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-1.5 mb-1"><TrendingUp className="h-4 w-4 text-slate-400" /> Site visits</p>
          <p className="text-xs text-slate-600">{data.analytics_note}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pulse-most-viewed-note">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-1.5 mb-1"><Eye className="h-4 w-4 text-slate-400" /> Most viewed launch content</p>
          <p className="text-xs text-slate-600">{data.most_viewed_note}</p>
        </div>
      </div>
    </div>
  );
}
